import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getAdminEmailLogs } from 'utils/api/admin';

const TYPE_LABEL = {
  guideline: '안내',
  password: '비밀번호',
  delete: '휴면전환',
  alert: '급매알림',
};
const STATUS_LABEL = { sent: '발송완료', failed: '실패' };

const EmailLogs = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [recipient, setRecipient] = useState('');
  const [recipientInput, setRecipientInput] = useState('');
  const [page, setPage] = useState(1);
  const size = 20;

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, type, status, recipient]);

  const fetchLogs = async () => {
    try {
      const res = await getAdminEmailLogs({
        type,
        status,
        recipient,
        page,
        size,
      });
      setLogs(res.results.logs);
      setTotal(res.results.total);
    } catch (err) {
      console.error(err);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setRecipient(recipientInput.trim());
  };

  const totalPages = Math.ceil(total / size);
  const formatDate = (d) => (d ? new Date(d).toLocaleString('ko-KR') : '-');

  return (
    <Container>
      <Title>이메일 발송 내역</Title>
      <FilterRow as="form" onSubmit={onSearch}>
        <Select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPage(1);
          }}
        >
          <option value="">전체 종류</option>
          <option value="guideline">안내</option>
          <option value="password">비밀번호</option>
          <option value="delete">휴면전환</option>
          <option value="alert">급매알림</option>
        </Select>
        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">전체 상태</option>
          <option value="sent">발송완료</option>
          <option value="failed">실패</option>
        </Select>
        <Input
          type="text"
          placeholder="수신자 이메일 검색"
          value={recipientInput}
          onChange={(e) => setRecipientInput(e.target.value)}
        />
        <SearchBtn type="submit">검색</SearchBtn>
        <Info>총 {total}건</Info>
      </FilterRow>
      <Table>
        <thead>
          <tr>
            <Th>발송시각</Th>
            <Th>종류</Th>
            <Th>수신자</Th>
            <Th>제목</Th>
            <Th>상태</Th>
            <Th>에러 메시지</Th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log._id}>
              <Td>{formatDate(log.sentAt || log.createdAt)}</Td>
              <Td>{TYPE_LABEL[log.type] || log.type}</Td>
              <Td>{log.recipient}</Td>
              <Td style={{ maxWidth: '320px' }}>{log.subject}</Td>
              <Td>
                <StatusBadge status={log.status}>
                  {STATUS_LABEL[log.status] || log.status}
                </StatusBadge>
              </Td>
              <Td style={{ fontSize: '12px', color: '#e53e3e' }}>
                {log.errorMessage || '-'}
              </Td>
            </tr>
          ))}
          {logs.length === 0 && (
            <tr>
              <Td colSpan={6} style={{ textAlign: 'center', color: '#8f9bba' }}>
                발송 내역이 없습니다.
              </Td>
            </tr>
          )}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <Pagination>
          <PageBtn disabled={page <= 1} onClick={() => setPage(page - 1)}>
            이전
          </PageBtn>
          <PageInfo>
            {page} / {totalPages}
          </PageInfo>
          <PageBtn
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </PageBtn>
        </Pagination>
      )}
    </Container>
  );
};

const Container = styled.div``;
const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #232d42;
  margin-bottom: 24px;
`;
const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;
const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
`;
const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  min-width: 220px;
`;
const SearchBtn = styled.button`
  padding: 8px 16px;
  border: 1px solid #232d42;
  border-radius: 8px;
  background: #232d42;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
`;
const Info = styled.div`
  font-size: 13px;
  color: #8f9bba;
  margin-left: auto;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;
const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #8f9bba;
  border-bottom: 1px solid #e4e4e4;
  background: #fafafa;
`;
const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #232d42;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
`;
const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ status }) =>
    status === 'sent'
      ? '#e6ffed'
      : status === 'failed'
      ? '#fff5f5'
      : '#f0f0f0'};
  color: ${({ status }) =>
    status === 'sent'
      ? '#38a169'
      : status === 'failed'
      ? '#e53e3e'
      : '#8f9bba'};
`;
const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
`;
const PageBtn = styled.button`
  padding: 6px 16px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;
const PageInfo = styled.span`
  font-size: 13px;
  color: #8f9bba;
`;

export default EmailLogs;
